import styled from 'styled-components';
import {
  Page,
  Container,
  Section,
  Eyebrow,
  Display,
  SectionTitle,
  Card,
  Grid,
  Mono,
  Tag,
  Facts,
  PrimaryButton,
  GhostButton,
  CtaBand,
  DISCORD_DM,
} from '../Layout/PageLayout';

const STRENGTHS = [
  ['Backend architecture', 'Data flow and persistence, organised into modules with clear boundaries.'],
  ['Production scripting', 'Gameplay systems written so that a team can extend and debug them.'],
  ['Performance under load', 'Less replication and simpler networking. Bottlenecks are found and fixed before they affect live servers.'],
];

const STACK = [
  ['Roblox stack', ['Luau', 'DataStore', 'ModuleScripts', 'Remote architecture', 'Live systems', 'Optimization']],
  ['Tooling', ['Rojo', 'Fusion', 'Wally', 'Selene', 'Git']],
  ['Team process', ['Code reviews', 'Milestone delivery', 'Long-term projects', 'Production-ready handoff']],
];

const FACTS = [
  ['EXPERIENCE', '4+ years on platform'],
  ['FOCUS', 'Backend & performance'],
  ['TIME ZONE', 'UTC+1 / CET'],
];

// Illustration generated as SVG (public/images/about-art.svg); replace the file to use a photo instead.
const Avatar = styled.img`
  flex: 0 1 22.5rem;
  min-width: 16.25rem;
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  display: block;
`;

const StrengthCard = styled(Card)`
  padding: 2rem;

  h3 {
    font-size: 1.375rem;
    margin: 2.5rem 0 0.75rem;
  }
`;

const StackRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2.5rem;
  padding: 1.75rem 0;
  border-top: 1px solid var(--border);
  align-items: center;

  &:last-child {
    border-bottom: 1px solid var(--border);
  }

  h3 {
    flex: 0 0 13.75rem;
    font-family: var(--font-sans);
    font-size: 1.125rem;
    letter-spacing: 0;
  }

  div {
    flex: 1 1 25rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const About = () => (
  <Page>
    <Section style={{ paddingTop: '6rem', paddingBottom: '6rem' }}>
      <Container style={{ display: 'flex', flexWrap: 'wrap', gap: '3.5rem', alignItems: 'center' }}>
        <Avatar src="/images/about-art.svg" alt="" width="360" height="450" />
        <div style={{ flex: '1 1 32.5rem', minWidth: 0 }}>
          <Eyebrow>About</Eyebrow>
          <Display style={{ fontSize: 'clamp(2.375rem, 5vw, 3.75rem)', lineHeight: 1.05, marginBottom: '1.75rem' }}>
            Backend work for Roblox games with real players.
          </Display>
          <p style={{ fontSize: '1.125rem', marginBottom: '1.25rem' }}>
            My name is Skeered. I write backend systems and gameplay code for Roblox, and most of my projects are games
            that already have players and regular updates.
          </p>
          <p style={{ fontSize: '1.125rem', marginBottom: '2.25rem' }}>
            I design each system so that other developers can read and extend it later. A clear structure also makes a
            codebase easier to maintain.
          </p>
          <Facts>
            {FACTS.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </Facts>
        </div>
      </Container>
    </Section>

    <Section $band style={{ borderBottom: 0 }}>
      <Container>
        <Eyebrow>Strengths</Eyebrow>
        <SectionTitle style={{ marginBottom: '3.5rem' }}>What I work on.</SectionTitle>
        <Grid $min="18.75rem">
          {STRENGTHS.map(([title, text], i) => (
            <StrengthCard key={title}>
              <Mono $dim style={{ fontSize: '0.8125rem' }}>
                {String(i + 1).padStart(2, '0')}
              </Mono>
              <h3>{title}</h3>
              <p>{text}</p>
            </StrengthCard>
          ))}
        </Grid>
      </Container>
    </Section>

    <Section $ruled>
      <Container>
        <Eyebrow>Stack &amp; workflow</Eyebrow>
        <SectionTitle style={{ marginBottom: '3.5rem' }}>Tools and workflow.</SectionTitle>
        {STACK.map(([title, tags]) => (
          <StackRow key={title}>
            <h3>{title}</h3>
            <div>
              {tags.map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </div>
          </StackRow>
        ))}
      </Container>
    </Section>

    <CtaBand
      title="Does your game need backend work?"
      text="Send me a message on Discord with a short description of the problem."
    >
      <PrimaryButton as="a" href={DISCORD_DM} target="_blank" rel="noopener noreferrer">
        Message on Discord
      </PrimaryButton>
      <GhostButton to="/portfolio">See the work</GhostButton>
    </CtaBand>
  </Page>
);

export default About;
